import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    appendLinkElement,
    getCanonical,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
} from "@insite/mobius/utilities/linkElementsManipulation";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { COLORS, ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";

export interface PaginationPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** String describing the URL of the previous page in the series */
        prevPage?: string;
        /** String describing the the URL of the next page in the series */
        nextPage?: string;
    }
>;

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const responsiveStyles = css`
    margin: 4px 0;
    width: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
`;

const iconButtonStyles = css`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    width: fit-content;
                `,
                null,
                null,
                css`
                    width: fit-content;
                `,
                null,
            ],
            "min",
        )};
    justify-content: center;
`;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    flex-direction: column;
                    gap: 8px;
                `,
                css`
                    flex-wrap: wrap;
                `,
                null,
                null,
            ],
            "max",
        )};
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    margin: 4px 0;
                `,
                null,
                null,
                css`
                    margin: 16px 0;
                `,
                null,
            ],
            "min",
        )};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    ${({ theme }: { theme: BaseTheme }) => {
        const P = (ELEMENTS as any).pagination;
        if (P?.hideFirstLast !== "true") return "";
        return css`
            ${P.containerGap ? `gap: ${P.containerGap};` : ""}
            ${breakpointMediaQueries(theme, [
                css`
                    margin-top: 20px;
                    flex-wrap: wrap;
                `,
                css`
                    margin-top: 20px;
                    flex-wrap: nowrap;
                `,
                css`
                    margin-top: 20px;
                    flex-wrap: nowrap;
                `,
                null,
                null,
            ])}
        `;
    }}
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, responsiveStyles, null, null], "max")};

    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            null,
            null,
            css`
                flex-basis: 60%;
            `,
            null,
            null,
        ])};

    ul {
        list-style: none;
    }
    li {
        display: inline-block;
        button {
            width: auto;
            span {
                color: #06060c;
            }
        }
        button[aria-label^="Current"] {
            background: ${getColor("primary")};
            border-radius: 0;
            &:hover {
                background: ${getColor("text.accent")};
            }
            span {
                color: ${getColor("secondary.contrast")};
            }
        }
    }
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                width: 100% !important;
                max-width: 100%;
                ul {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    /* Centered with a fixed gap: space-between pushed the arrows and numbers
                       to the screen edges when there were only a couple of pages. */
                    justify-content: center;
                    flex-wrap: nowrap;
                    gap: 8px !important;
                }
                li button {
                    padding-left: 4px !important;
                    padding-right: 4px !important;
                }
            `,
            null,
            null,
            null,
            null,
        ])};
    ${({ theme }: { theme: BaseTheme }) => {
        const P = (ELEMENTS as any).pagination;
        if (P?.hideFirstLast !== "true") return "";
        return css`
            ul {
                gap: ${P.gap || "16px"};
                display: flex;
                align-items: center;
            }
            li {
                button {
                    width: fit-content;
                    padding: ${P.padding || "0"};
                    min-height: ${P.minHeight || "unset"};
                    ${P.buttonBorder ? `border: ${P.buttonBorder};` : ""}
                    ${P.buttonBackground ? `background: ${P.buttonBackground};` : ""}
                    span {
                        font-style: normal;
                        font-weight: 400;
                        font-size: ${P.size || "15px"};
                        line-height: 24px;
                        color: ${P.pageColor || "#333"};
                    }
                }
            }
            ul > li:first-child,
            ul > li:last-child {
                display: none;
            }
            button[data-test-selector^="pagination_"] {
                background: transparent !important;
                border: none !important;
                padding: 0 !important;
                min-height: unset !important;
                ${P.pageColor ? `color: ${P.pageColor} !important;` : ""}
                &:not(:disabled):hover {
                    background: transparent !important;
                    border: none !important;
                }
                &:disabled {
                    background: transparent !important;
                    border: none !important;
                    ${P.pageColor ? `color: ${P.pageColor} !important;` : ""}
                    ${P.arrowDisabledOpacity ? `opacity: ${P.arrowDisabledOpacity} !important;` : ""}
                }
            }
            button[aria-label^="Current"] {
                min-height: ${P.activeSize || "24px"} !important;
                ${P.activeSize
                    ? `
                    width: ${P.activeSize} !important;
                    height: ${P.activeSize};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `
                    : ""}
                ${P.activeBorderRadius ? `border-radius: ${P.activeBorderRadius} !important;` : ""}
                ${P.activeBackground
                    ? `
                    background: ${P.activeBackground} !important;
                    border-color: ${P.activeBackground} !important;
                `
                    : ""}
                span {
                    color: ${P.activeColor || "white"} !important;
                    ${P.activeSize ? "font-weight: 500;" : ""}
                }
                ${P.activeBackground
                    ? `
                    &:hover {
                        background: ${P.activeBackground} !important;
                        border-color: ${P.activeBackground} !important;
                    }
                `
                    : ""}
            }
            ${breakpointMediaQueries(theme, [
                css`
                    width: auto;
                `,
                css`
                    width: auto;
                `,
                css`
                    width: auto;
                `,
                null,
                null,
            ])}
        `;
    }}
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, responsiveStyles, null, null], "max")};
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            null,
            null,
            css`
                flex-basis: 40%;
            `,
            null,
            null,
        ])};
    label {
        color: ${getColor("text.main")};
    }
    ${({ theme }: { theme: BaseTheme }) => {
        const P = (ELEMENTS as any).pagination;
        if (P?.hideFirstLast !== "true") return "";
        return css`
            label {
                font-style: normal;
                font-weight: ${P.labelFontWeight || TYPOGRAPHY.weight.base} !important;
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                color: ${COLORS.lightBlack};
                padding: 2px 15px 0 0;
            }
            > div[class^="FormInputWrapper"] {
                max-width: 65px;
            }
            select {
                /* Beats the brand-wide select height applied in Start.tsx. */
                height: 28px !important;
                padding: 0 10px !important;
            }
            span {
                top: -3px !important;
                padding: 0 8px !important;
            }
            ${breakpointMediaQueries(theme, [
                css`
                    width: auto;
                `,
                css`
                    width: auto;
                `,
                css`
                    width: auto;
                `,
                null,
                null,
            ])}
        `;
    }}
    ${injectCss}
`;

const Ellipsis = styled.li<InjectableCss>`
    ${injectCss}
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        mergeCss,
        prevPage,
        nextPage,
        ...otherProps
    } = props;

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);

    React.useEffect(() => {
        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
            prevPage,
            nextPage,
            convertToNumberIfString(possibleStringCurrentPage),
            "",
        );
        const prevCanonical = `${getCanonical()}${prevQueryString}`;
        const nextCanonical = `${getCanonical()}${nextQueryString}`;
        updateCanonical(currentQueryString);
        const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
        const prevNotFromProducts = prevPage?.indexOf("/api/v2/products?") < 0 && prevPage?.indexOf("search=") < 0;
        const nextNotFromProducts = nextPage?.indexOf("/api/v2/products?") < 0 && nextPage?.indexOf("search=") < 0;

        if (prevPage && !hasPrev && prevNotFromProducts) {
            appendLinkElement("prev", prevCanonical);
        }

        if (nextPage && !hasNext && nextNotFromProducts) {
            appendLinkElement("next", nextCanonical);
        }

        return () => {
            const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
            if (prevPage && hasPrev && prevNotFromProducts) {
                removeLinkElement("prev", prevCanonical);
            }

            if (nextPage && hasNext && nextNotFromProducts) {
                removeLinkElement("next", nextCanonical);
            }
        };
    }, [prevPage, nextPage]);

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
    };

    const { spreadProps, applyProp, applyStyledProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const navIconsSrc = spreadProps("navIconsSrc");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.pagination?.defaultProps?.mergeCss;

    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    const paginationWindow = parseInt((ELEMENTS as any).pagination?.paginationWindow, 10) || 0;
    let pagesToDisplay: ("e" | number)[] = [];
    if (paginationWindow >= 5) {
        if (finalPageIndex <= paginationWindow + 2) {
            for (let i = 1; i <= finalPageIndex; i += 1) {
                pagesToDisplay.push(i);
            }
        } else if (currentPage <= paginationWindow - 1) {
            for (let i = 1; i <= paginationWindow; i += 1) {
                pagesToDisplay.push(i);
            }
            pagesToDisplay.push("e", finalPageIndex);
        } else if (currentPage >= finalPageIndex - (paginationWindow - 2)) {
            pagesToDisplay.push(1, "e");
            for (let i = finalPageIndex - (paginationWindow - 1); i <= finalPageIndex; i += 1) {
                pagesToDisplay.push(i);
            }
        } else {
            pagesToDisplay = [1, "e", currentPage - 1, currentPage, currentPage + 1, currentPage + 2, "e", finalPageIndex];
        }
    } else if (finalPageIndex < 6) {
        for (let i = 1; i <= finalPageIndex; i += 1) {
            pagesToDisplay.push(i);
        }
    } else if (currentPage < 4) {
        pagesToDisplay = [1, 2, 3, 4, "e", finalPageIndex];
    } else if (currentPage >= 4 && currentPage < finalPageIndex - 2) {
        pagesToDisplay = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, "e", finalPageIndex];
    } else {
        pagesToDisplay = ["e", finalPageIndex - 4, finalPageIndex - 3, finalPageIndex - 2, finalPageIndex - 1, finalPageIndex];
    }

    const paginationButtons = pagesToDisplay.map((page, index) => {
        if (page === "e") {
            return (
                <Ellipsis key={`ellipsis-${index}`} css={applyStyledProp(["cssOverrides", "ellipsis"], resolvedMergeCss)}>
                    &hellip;
                </Ellipsis>
            );
        }
        return (
            <li key={`page${page}`}>
                <Button
                    {...buttonDisplayProps(props, {
                        moreCss: numericButtonStyles,
                        page,
                        cssOverrides,
                        buttonProps,
                        currentPageButtonVariant,
                    })}
                    type="button"
                >
                    {page.toString()}
                </Button>
            </li>
        );
    });

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navIcon = (pageIndex: number, icon: string | React.ComponentType, ariaLabel: string, disabledFlag: boolean, dataTestSelector?: string) => (
        <Button
            variant="secondary"
            {...buttonDisplayProps(props, {
                page: -1,
                moreCss: iconButtonStyles,
                cssOverrides,
                buttonProps,
                currentPageButtonVariant,
            })}
            aria-label={ariaLabel}
            disabled={disabledFlag}
            onClick={() => onChangePage(pageIndex)}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
            type="button"
        >
            <VisuallyHidden>{ariaLabel}</VisuallyHidden>
            <ButtonIcon size={15} src={icon} />
        </Button>
    );

    return (
        <PaginationStyle css={applyStyledProp(["cssOverrides", "pagination"], resolvedMergeCss)} {...omitSingle(otherProps, "cssOverrides")}>
            {resultsPerPageOptions?.length > 0 && (
                <PerPageSelect css={applyStyledProp(["cssOverrides", "perPageSelect"], resolvedMergeCss)}>
                    <Select
                        onChange={changeResultsPerPage}
                        label={resultsPerPageLabel || translate("Results Per Page")}
                        labelPosition="left"
                        value={resultsPerPage}
                        {...spreadProps("selectProps" as any)}
                        data-test-selector="pagination_resultsPerPage"
                    >
                        {resultsPerPageOptions.map((n: string) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </Select>
                </PerPageSelect>
            )}
            <LinkList
                role="navigation"
                aria-label={translate("Pagination navigation")}
                css={applyStyledProp(["cssOverrides", "linkList"], resolvedMergeCss)}
                data-test-selector={`pagination_currentPage_${currentPage}`}
            >
                <ul>
                    <li>{navIcon(1, navIconsSrc.firstPage, translate("First page"), backDisabled, "pagination_firstButton")}</li>
                    <li>{navIcon(currentPage - 1, navIconsSrc.previousPage, translate("Previous page"), backDisabled, "pagination_previousButton")}</li>
                    {paginationButtons}
                    <li>{navIcon(currentPage + 1, navIconsSrc.nextPage, translate("Next page"), forwardDisabled, "pagination_nextButton")}</li>
                    <li>{navIcon(finalPageIndex, navIconsSrc.lastPage, translate("Last page"), forwardDisabled, "pagination_lastButton")}</li>
                </ul>
            </LinkList>
        </PaginationStyle>
    );
});

/** @component */
export default withTheme(Pagination) as React.ComponentType<PaginationComponentProps>;
export { Pagination, PaginationStyle };
